import React, { useState, useEffect, useMemo } from 'react'
import { db } from '../firebase'
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
  getDoc,
  writeBatch
} from 'firebase/firestore'
import LoadingSpinner from '../components/ui/LoadingSpinner'

function EditPrice () {
  const [showConfirm, setShowConfirm] = useState(false)
  const [showRemoveLastPriceConfirm, setShowRemoveLastPriceConfirm] =
    useState(false)
  const [showParams, setShowParams] = useState(true)
  const [wrestlers, setWrestlers] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState('')
  const [sortOrder, setSortOrder] = useState('desc')
  const [sortField, setSortField] = useState('quotation')
  const [factionCounts, setFactionCounts] = useState({})
  const [visibilityFilter, setVisibilityFilter] = useState('active') // 'all', 'active', 'inactive'

  // Campi modificabili per ogni wrestler
  const [editableFields, setEditableFields] = useState({
    position: {},
    storyline: {}
  })

  const [saveProgress, setSaveProgress] = useState(0)
  const [currentSavingWrestler, setCurrentSavingWrestler] = useState('')
  const [totalToSave, setTotalToSave] = useState(0)
  const [savedCount, setSavedCount] = useState(0)
  const [savingSingleId, setSavingSingleId] = useState('')
  const [singleSaveMessage, setSingleSaveMessage] = useState({})

  // Aggiungo stato per le modifiche manuali delle quotazioni
  const [manualAdjustments, setManualAdjustments] = useState({})

  const getPriceEntryValue = priceEntry => {
    const rawValue =
      typeof priceEntry === 'number'
        ? priceEntry
        : priceEntry?.value ?? priceEntry?.price
    const value = Number(rawValue)
    return Number.isFinite(value) ? value : 0
  }

  useEffect(() => {
    const fetchWrestlersAndFactions = async () => {
      try {
        setLoading(true)
        const snap = await getDocs(collection(db, 'wrestlers'))
        const wrestlerDocs = snap.docs

        const factionsSnap = await getDocs(collection(db, 'factions'))
        const factions = factionsSnap.docs.map(doc => doc.data())

        const counts = {}
        factions.forEach(faction => {
          ;(faction.costs || []).forEach(m => {
            if (m.wrestlerId) {
              counts[m.wrestlerId] = (counts[m.wrestlerId] || 0) + 1
            }
          })
        })
        setFactionCounts(counts)

        const allWrestlers = []

        for (const docItem of wrestlerDocs) {
          const wrestlerData = docItem.data()
          const prices = wrestlerData.prices || []
          const isActive = wrestlerData.active === true;

          const currentPrice =
            prices.length > 0 ? getPriceEntryValue(prices[prices.length - 1]) : 0

          const championTypes = Array.isArray(wrestlerData.championTypes)
            ? wrestlerData.championTypes
            : []
          const championType =
            championTypes.length > 0 ? championTypes[0] : null
          const isChampion = championTypes.length > 0
          const worldTitles = [
            'WWE Champion',
            'World Heavyweight Champion',
            "WWE Women's Champion",
            "Women's World Champion"
          ]
          const isWorldChampion = championTypes.some(ct =>
            worldTitles.includes(ct)
          )

          allWrestlers.push({
            id: docItem.id,
            name: wrestlerData.name || wrestlerData.nome || 'Sconosciuto',
            score: wrestlerData.score ?? 0,
            factions: counts[docItem.id] || 0,
            position: wrestlerData.position || 0,
            storyline: wrestlerData.storyline || 0,
            isChampion,
            isWorldChampion,
            championType,
            quotation: currentPrice,
            prices: prices,
            active: isActive,
            calculatedQuotation: calculateQuotationForWrestler({
              position: wrestlerData.position || 0,
              storyline: wrestlerData.storyline || 0,
              isChampion,
              isWorldChampion,
              factions: counts[docItem.id] || 0,
              score: wrestlerData.score ?? 0
            })
          })
        }

        setWrestlers(allWrestlers)

        const initialEditableFields = {
          position: {},
          storyline: {}
        }
        allWrestlers.forEach(wrestler => {
          initialEditableFields.position[wrestler.id] = wrestler.position
          initialEditableFields.storyline[wrestler.id] = wrestler.storyline
        })
        setEditableFields(initialEditableFields)

        // Inizializza manualAdjustments
        const initialManualAdjustments = {}
        allWrestlers.forEach(wrestler => {
          initialManualAdjustments[wrestler.id] = 0
        })
        setManualAdjustments(initialManualAdjustments)
      } catch (err) {
        console.error('Errore nel caricamento:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchWrestlersAndFactions()
  }, [])

  const calculateQuotationForWrestler = ({
    position,
    storyline,
    isChampion,
    isWorldChampion,
    factions,
    score
  }) => {
    const scoreContribution = Math.min(score, 300) * 0.1
    let quotation =
      position * 4 +
      storyline * 6 +
      (isChampion ? 3 : 0) +
      (isWorldChampion ? 6 : 0) +
      factions * 0.2 +
      scoreContribution

    quotation = Math.floor(quotation + 0.5)
    quotation = Math.max(1, Math.min(quotation, 90))

    return quotation
  }

  const getCurrentPrice = wrestler => {
    if (
      !wrestler.prices ||
      !Array.isArray(wrestler.prices) ||
      wrestler.prices.length === 0
    ) {
      return 0
    }
    const lastPrice = wrestler.prices[wrestler.prices.length - 1]
    return getPriceEntryValue(lastPrice)
  }

  // Funzione per calcolare la quotazione finale con eventuali aggiustamenti manuali
  const getFinalQuotation = wrestler => {
    const calculatedQuotation = calculateQuotationForWrestler({
      position:
        editableFields.position[wrestler.id] !== undefined
          ? editableFields.position[wrestler.id]
          : wrestler.position,
      storyline:
        editableFields.storyline[wrestler.id] !== undefined
          ? editableFields.storyline[wrestler.id]
          : wrestler.storyline,
      isChampion: wrestler.isChampion,
      isWorldChampion: wrestler.isWorldChampion,
      factions: factionCounts[wrestler.id] || 0,
      score: wrestler.score
    })

    // Aggiungi l'aggiustamento manuale
    const adjustment = manualAdjustments[wrestler.id] || 0
    let finalQuotation = calculatedQuotation + adjustment

    // Applica i limiti
    finalQuotation = Math.max(1, Math.min(finalQuotation, 90))

    return finalQuotation
  }

  // Funzioni per modificare gli aggiustamenti manuali
  const incrementAdjustment = wrestlerId => {
    setManualAdjustments(prev => {
      const current = prev[wrestlerId] || 0
      const calculatedQuotation = calculateQuotationForWrestler({
        position:
          editableFields.position[wrestlerId] !== undefined
            ? editableFields.position[wrestlerId]
            : wrestlers.find(w => w.id === wrestlerId)?.position || 0,
        storyline:
          editableFields.storyline[wrestlerId] !== undefined
            ? editableFields.storyline[wrestlerId]
            : wrestlers.find(w => w.id === wrestlerId)?.storyline || 0,
        isChampion:
          wrestlers.find(w => w.id === wrestlerId)?.isChampion || false,
        isWorldChampion:
          wrestlers.find(w => w.id === wrestlerId)?.isWorldChampion || false,
        factions: factionCounts[wrestlerId] || 0,
        score: wrestlers.find(w => w.id === wrestlerId)?.score || 0
      })

      const newAdjustment = current + 1
      const finalQuotation = calculatedQuotation + newAdjustment

      // Non permettere di superare 90
      if (finalQuotation > 90) return prev

      return {
        ...prev,
        [wrestlerId]: newAdjustment
      }
    })
  }

  const decrementAdjustment = wrestlerId => {
    setManualAdjustments(prev => {
      const current = prev[wrestlerId] || 0
      const calculatedQuotation = calculateQuotationForWrestler({
        position:
          editableFields.position[wrestlerId] !== undefined
            ? editableFields.position[wrestlerId]
            : wrestlers.find(w => w.id === wrestlerId)?.position || 0,
        storyline:
          editableFields.storyline[wrestlerId] !== undefined
            ? editableFields.storyline[wrestlerId]
            : wrestlers.find(w => w.id === wrestlerId)?.storyline || 0,
        isChampion:
          wrestlers.find(w => w.id === wrestlerId)?.isChampion || false,
        isWorldChampion:
          wrestlers.find(w => w.id === wrestlerId)?.isWorldChampion || false,
        factions: factionCounts[wrestlerId] || 0,
        score: wrestlers.find(w => w.id === wrestlerId)?.score || 0
      })

      const newAdjustment = current - 1
      const finalQuotation = calculatedQuotation + newAdjustment

      // Non permettere di scendere sotto 1
      if (finalQuotation < 1) return prev

      return {
        ...prev,
        [wrestlerId]: newAdjustment
      }
    })
  }

  const resetAdjustment = wrestlerId => {
    setManualAdjustments(prev => ({
      ...prev,
      [wrestlerId]: 0
    }))
  }

  const getPositionUpdatedFields = wrestler => {
    const position =
      editableFields.position[wrestler.id] !== undefined
        ? editableFields.position[wrestler.id]
        : wrestler.position
    const updatedFields = {}

    if (position !== wrestler.position) {
      updatedFields.position = position
    }

    return updatedFields
  }

  const commitWrestlerUpdateBatches = async updates => {
    const batchSize = 450

    for (let index = 0; index < updates.length; index += batchSize) {
      const batch = writeBatch(db)
      updates
        .slice(index, index + batchSize)
        .forEach(({ wrestler, updatedFields }) => {
          batch.update(doc(db, 'wrestlers', wrestler.id), updatedFields)
        })
      await batch.commit()
    }
  }

  const doSavePositionFields = async () => {
    try {
      setSaving(true)
      setSaveMessage('')

      const updates = wrestlers
        .map(wrestler => ({
          wrestler,
          updatedFields: getPositionUpdatedFields(wrestler)
        }))
        .filter(update => Object.keys(update.updatedFields).length > 0)

      if (updates.length === 0) {
        setSaveMessage('Nessuna modifica di posizione da salvare.')
        return
      }

      await commitWrestlerUpdateBatches(updates)

      const updatesById = updates.reduce((acc, { wrestler, updatedFields }) => {
        acc[wrestler.id] = updatedFields
        return acc
      }, {})

      setWrestlers(prev =>
        prev.map(w =>
          updatesById[w.id]
            ? {
                ...w,
                ...updatesById[w.id]
              }
            : w
        )
      )

      setEditableFields(prev => {
        const nextFields = {
          position: { ...prev.position },
          storyline: { ...prev.storyline }
        }

        updates.forEach(({ wrestler, updatedFields }) => {
          if (updatedFields.position !== undefined) {
            nextFields.position[wrestler.id] = updatedFields.position
          }
        })

        return nextFields
      })

      setSaveMessage(
        `Posizione salvata per ${updates.length} wrestler. Quotazioni e storyline non modificate.`
      )
    } catch (error) {
      console.error('Errore nel salvataggio posizione:', error)
      setSaveMessage('Errore nel salvataggio della posizione.')
    } finally {
      setSaving(false)
    }
  }

  const doRemoveLastPriceRecords = async () => {
    try {
      setSaving(true)
      setSaveMessage('')

      const snap = await getDocs(collection(db, 'wrestlers'))
      const updates = snap.docs
        .map(docItem => {
          const data = docItem.data()
          const prices = Array.isArray(data.prices) ? data.prices : []

          if (prices.length === 0) return null

          const updatedPrices = prices.slice(0, -1)
          const nextPrice =
            updatedPrices.length > 0
              ? getPriceEntryValue(updatedPrices[updatedPrices.length - 1])
              : 0

          return {
            wrestler: {
              id: docItem.id,
              name: data.name || data.nome || 'Wrestler'
            },
            updatedPrices,
            nextPrice,
            updatedFields: {
              prices: updatedPrices,
              price: nextPrice,
              updatedAt: serverTimestamp()
            }
          }
        })
        .filter(Boolean)

      if (updates.length === 0) {
        setSaveMessage('Nessun record di quotazione da rimuovere.')
        return
      }

      await commitWrestlerUpdateBatches(updates)

      const updatesById = updates.reduce((acc, update) => {
        acc[update.wrestler.id] = update
        return acc
      }, {})

      setWrestlers(prev =>
        prev.map(w => {
          const update = updatesById[w.id]
          if (!update) return w

          return {
            ...w,
            prices: update.updatedPrices,
            quotation: update.nextPrice
          }
        })
      )

      setSaveMessage(
        `Ultimo record quotazioni rimosso per ${updates.length} wrestler.`
      )
    } catch (error) {
      console.error('Errore nella rimozione ultimo record quotazioni:', error)
      setSaveMessage('Errore nella rimozione ultimo record quotazioni.')
    } finally {
      setSaving(false)
    }
  }

  const doSaveChanges = async () => {
    try {
      setSaving(true)
      setSaveMessage('')
      setSaveProgress(0)
      setSavedCount(0)
      setCurrentSavingWrestler('')

      const wrestlersToSave = []
      const updatedWrestlersData = []

      for (const wrestler of wrestlers) {
        const newPosition =
          editableFields.position[wrestler.id] !== undefined
            ? editableFields.position[wrestler.id]
            : wrestler.position
        const newStoryline =
          editableFields.storyline[wrestler.id] !== undefined
            ? editableFields.storyline[wrestler.id]
            : wrestler.storyline

        // Calcola la nuova quotazione FINALE (inclusi aggiustamenti manuali)
        const newQuotation = getFinalQuotation(wrestler)

        // Recupera l'array prices esistente
        const wrestlerDoc = await getDoc(doc(db, 'wrestlers', wrestler.id))
        const wrestlerData = wrestlerDoc.data()
        const existingPrices = wrestlerData.prices || []

        const newPriceEntry = {
          value: newQuotation,
          date: new Date()
        }

        let updatedPrices = [...existingPrices, newPriceEntry]
        if (updatedPrices.length > 5) {
          updatedPrices = updatedPrices.slice(-5)
        }

        const currentQuotation = getCurrentPrice(wrestler)
        const hasQuotationChanged = newQuotation !== currentQuotation
        const hasPositionChanged = newPosition !== wrestler.position
        const hasStorylineChanged = newStoryline !== wrestler.storyline

        if (hasQuotationChanged || hasPositionChanged || hasStorylineChanged) {
          wrestlersToSave.push({
            ...wrestler,
            id: wrestler.id,
            name: wrestler.name,
            newPosition,
            newStoryline,
            newQuotation,
            updatedPrices,
            hasQuotationChanged
          })
        }

        updatedWrestlersData.push({
          id: wrestler.id,
          newPosition,
          newStoryline,
          newQuotation,
          updatedPrices,
          hasQuotationChanged
        })
      }

      setTotalToSave(wrestlersToSave.length)

      if (wrestlersToSave.length === 0) {
        setSaveMessage('Nessuna modifica da salvare.')
        setSaving(false)
        return
      }

      for (let i = 0; i < wrestlersToSave.length; i++) {
        const wrestler = wrestlersToSave[i]

        setCurrentSavingWrestler(wrestler.name)
        setSavedCount(i + 1)
        setSaveProgress(Math.round(((i + 1) / wrestlersToSave.length) * 100))

        const updatedFields = {}

        updatedFields.prices = wrestler.updatedPrices

        if (wrestler.newPosition !== wrestler.position) {
          updatedFields.position = wrestler.newPosition
        }
        if (wrestler.newStoryline !== wrestler.storyline) {
          updatedFields.storyline = wrestler.newStoryline
        }

        updatedFields.price = wrestler.newQuotation
        updatedFields.updatedAt = serverTimestamp()

        await updateDoc(doc(db, 'wrestlers', wrestler.id), updatedFields)
      }

      // Aggiorna i wrestler nello stato locale
      setWrestlers(prev =>
        prev.map(w => {
          const updateData = updatedWrestlersData.find(ud => ud.id === w.id)
          if (!updateData) return w

          const updatedWrestler = { ...w }
          updatedWrestler.position = updateData.newPosition
          updatedWrestler.storyline = updateData.newStoryline
          updatedWrestler.quotation = updateData.newQuotation
          updatedWrestler.calculatedQuotation = updateData.newQuotation
          updatedWrestler.prices = updateData.updatedPrices

          return updatedWrestler
        })
      )

      // Reset degli aggiustamenti manuali dopo il salvataggio
      const newEditableFields = { position: {}, storyline: {} }
      updatedWrestlersData.forEach(update => {
        newEditableFields.position[update.id] = update.newPosition
        newEditableFields.storyline[update.id] = update.newStoryline
      })
      setEditableFields(newEditableFields)

      // Reset di tutti gli aggiustamenti manuali
      const resetAdjustments = {}
      wrestlers.forEach(w => {
        resetAdjustments[w.id] = 0
      })
      setManualAdjustments(resetAdjustments)

      setSaveMessage(
        `Modifiche salvate con successo! ${wrestlersToSave.length} wrestler aggiornati.`
      )
    } catch (error) {
      console.error('Errore nel salvataggio:', error)
      setSaveMessage('Errore nel salvataggio delle modifiche.')
    } finally {
      setTimeout(() => {
        setSaving(false)
        setSaveProgress(0)
        setCurrentSavingWrestler('')
        setTotalToSave(0)
        setSavedCount(0)
      }, 3000)
    }
  }

  const doSaveSingle = async wrestler => {
    try {
      setSavingSingleId(wrestler.id)
      setSingleSaveMessage(prev => ({ ...prev, [wrestler.id]: '' }))

      const newPosition =
        editableFields.position[wrestler.id] !== undefined
          ? editableFields.position[wrestler.id]
          : wrestler.position
      const newStoryline =
        editableFields.storyline[wrestler.id] !== undefined
          ? editableFields.storyline[wrestler.id]
          : wrestler.storyline

      // Usa la quotazione finale (inclusi aggiustamenti manuali)
      const newQuotation = getFinalQuotation(wrestler)

      const wrestlerDoc = await getDoc(doc(db, 'wrestlers', wrestler.id))
      const wrestlerData = wrestlerDoc.data() || {}
      const existingPrices = wrestlerData.prices || []

      const newPriceEntry = {
        value: newQuotation,
        date: new Date()
      }

      let updatedPrices = [...existingPrices, newPriceEntry]
      if (updatedPrices.length > 5) {
        updatedPrices = updatedPrices.slice(-5)
      }

      const updatedFields = {
        prices: updatedPrices,
        price: newQuotation,
        updatedAt: serverTimestamp()
      }

      if (newPosition !== wrestler.position) {
        updatedFields.position = newPosition
      }
      if (newStoryline !== wrestler.storyline) {
        updatedFields.storyline = newStoryline
      }

      await updateDoc(doc(db, 'wrestlers', wrestler.id), updatedFields)

      // Aggiorna stato locale
      setWrestlers(prev =>
        prev.map(w => {
          if (w.id !== wrestler.id) return w
          return {
            ...w,
            position: newPosition,
            storyline: newStoryline,
            quotation: newQuotation,
            calculatedQuotation: newQuotation,
            prices: updatedPrices
          }
        })
      )

      // Aggiorna i campi modificabili
      setEditableFields(prev => ({
        ...prev,
        position: { ...prev.position, [wrestler.id]: newPosition },
        storyline: { ...prev.storyline, [wrestler.id]: newStoryline }
      }))

      // Reset dell'aggiustamento manuale per questo wrestler
      setManualAdjustments(prev => ({
        ...prev,
        [wrestler.id]: 0
      }))

      setSingleSaveMessage(prev => ({ ...prev, [wrestler.id]: 'Salvato ✓' }))
      setSaveMessage(
        `Quotazione di ${wrestler.name} aggiornata a ${newQuotation}`
      )
    } catch (err) {
      console.error('Errore salvataggio singolo:', err)
      setSingleSaveMessage(prev => ({ ...prev, [wrestler.id]: 'Errore ❌' }))
      setSaveMessage(`Errore nel salvataggio di ${wrestler.name}`)
    } finally {
      setTimeout(() => {
        setSavingSingleId('')
        setTimeout(() => setSaveMessage(''), 3000)
      }, 1500)
    }
  }

  const handleFieldChange = (wrestlerId, field, value) => {
    setEditableFields(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [wrestlerId]: value
      }
    }))

    // Reset dell'aggiustamento manuale quando si modificano position o storyline
    if (field === 'position' || field === 'storyline') {
      resetAdjustment(wrestlerId)
    }
  }

  const saveChanges = () => {
    setShowConfirm(true)
  }

  const handleSort = field => {
    setSortOrder(prev =>
      sortField === field && prev === 'asc' ? 'desc' : 'asc'
    )
    setSortField(field)
  }

  const sortIcon = field => {
    if (sortField !== field) return null
    return sortOrder === 'asc' ? (
      <i className='bi bi-caret-up-fill ms-1' />
    ) : (
      <i className='bi bi-caret-down-fill ms-1' />
    )
  }

  const sortedWrestlers = useMemo(() => {
      let filtered = wrestlers;
  if (visibilityFilter === 'active') {
    filtered = wrestlers.filter(w => w.active === true);
  } else if (visibilityFilter === 'inactive') {
    filtered = wrestlers.filter(w => w.active === false);
  }
    return [...filtered].sort((a, b) => {
      let aValue, bValue
      if (sortField === 'quotation') {
        aValue = a.quotation || 0
        bValue = b.quotation || 0
      } else if (sortField === 'calculated') {
        aValue = getFinalQuotation(a)
        bValue = getFinalQuotation(b)
      } else {
        aValue = a[sortField] ?? (typeof a[sortField] === 'number' ? 0 : '')
        bValue = b[sortField] ?? (typeof b[sortField] === 'number' ? 0 : '')
      }

      let comparison
      if (sortOrder === 'asc') {
        comparison =
          typeof aValue === 'number'
            ? aValue - bValue
            : aValue.localeCompare(bValue)
      } else {
        comparison =
          typeof aValue === 'number'
            ? bValue - aValue
            : bValue.localeCompare(aValue)
      }

      if (comparison === 0) {
        return a.name.localeCompare(b.name)
      }

      return comparison
    })
}, [wrestlers, sortField, sortOrder, visibilityFilter, editableFields, manualAdjustments]);

  if (loading) return <LoadingSpinner fullPage={true} />

  return (
    <div className='container admin-page mt-4'>
      {showConfirm && (
        <div
          className='modal fade show'
          style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}
          tabIndex={-1}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Conferma Salvataggio</h5>
              </div>
              <div className='modal-body'>
                <p>
                  Verranno salvate <b>tutte le quotazioni</b> di{' '}
                  <b>tutti i wrestler</b>.<br />
                  Vuoi continuare?
                </p>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => setShowConfirm(false)}
                  disabled={saving}
                >
                  Annulla
                </button>
                <button
                  className='btn btn-success'
                  onClick={() => {
                    setShowConfirm(false)
                    doSaveChanges()
                  }}
                  disabled={saving}
                >
                  Conferma e Salva
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRemoveLastPriceConfirm && (
        <div
          className='modal fade show'
          style={{ display: 'block', background: 'rgba(0,0,0,0.4)' }}
          tabIndex={-1}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Rimuovi ultimo record quotazioni</h5>
              </div>
              <div className='modal-body'>
                <p>
                  Verra rimosso <b>l'ultimo record</b> dallo storico quotazioni
                  di tutti i wrestler. Il valore attuale tornera al record
                  precedente, oppure a 0 se non resta storico.
                </p>
                <p className='mb-0 text-danger fw-semibold'>
                  Vuoi continuare?
                </p>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => setShowRemoveLastPriceConfirm(false)}
                  disabled={saving}
                >
                  Annulla
                </button>
                <button
                  className='btn btn-danger'
                  onClick={() => {
                    setShowRemoveLastPriceConfirm(false)
                    doRemoveLastPriceRecords()
                  }}
                  disabled={saving}
                >
                  Rimuovi ultimo record
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h1 className='fw-bold'>Modifica Quotazioni Wrestler</h1>
        <div className='d-flex gap-3 align-items-center'>
          {/* Filtro visibilità */}
          <div className='d-flex align-items-center gap-2'>
            <label className='text-muted'>Mostra:</label>
            <select
              className='form-select form-select-sm w-auto'
              value={visibilityFilter}
              onChange={e => setVisibilityFilter(e.target.value)}
            >
              <option value='all'>Tutti</option>
              <option value='active'>Solo Attivi</option>
              <option value='inactive'>Solo Inattivi</option>
            </select>
          </div>

          <button
            className='btn btn-outline-primary'
            onClick={doSavePositionFields}
            disabled={saving}
          >
            {saving ? 'Salvataggio...' : 'Salva solo posizione'}
          </button>

          <button
            className='btn btn-outline-danger'
            onClick={() => setShowRemoveLastPriceConfirm(true)}
            disabled={saving}
          >
            <i className='bi bi-trash3 me-1' aria-hidden='true'></i>
            Rimuovi ultimo record
          </button>

          <button
            className='btn btn-success me-2'
            onClick={saveChanges}
            disabled={saving }
          >
            {saving ? 'Salvataggio...' : 'Salva Modifiche'}
          </button>
          {saveMessage && (
            <span
              className={`alert ${
                saveMessage.includes('Errore')
                  ? 'alert-danger'
                  : 'alert-success'
              } py-1 mb-0`}
            >
              {saveMessage}
            </span>
          )}
        </div>
      </div>

      <div className='card shadow-sm'>
        <div
          className='card-header bg-dark text-white py-2 d-flex justify-content-between align-items-center'
          style={{ cursor: 'pointer' }}
          onClick={() => setShowParams(v => !v)}
        >
          <h5 className='mb-0'>Parametri Quotazione</h5>
          <button
            className='btn btn-sm btn-light ms-2'
            type='button'
            tabIndex={-1}
            onClick={e => {
              e.stopPropagation()
              setShowParams(v => !v)
            }}
            aria-label={showParams ? 'Nascondi parametri' : 'Mostra parametri'}
          >
            <i
              className={`bi ${
                showParams ? 'bi-chevron-up' : 'bi-chevron-down'
              }`}
            ></i>
          </button>
        </div>
        {showParams && (
          <div className='card-body'>
            <p className='fw-bold mb-3'>
              Formula di calcolo:
              <span className='text-primary'>
                {' '}
                (Posizione × 4) + (Storyline × 6) + (Campione × 3) + (Campione
                Mondiale × 6) + (Fazioni × 0.2) + (min(Punti, 300) × 0.1)
              </span>
            </p>

            <div className='row'>
              <div className='col-md-4'>
                <div className='border rounded p-3 h-100'>
                  <h6 className='fw-bold border-bottom pb-2'>Posizione</h6>
                  <ul className='list-unstyled small mb-0'>
                    <li>
                      0: Non Tv/compare poco{' '}
                      <span className='float-end text-muted'>0 pts</span>
                    </li>
                    <li>
                      1: Jobber{' '}
                      <span className='float-end text-muted'>4 pts</span>
                    </li>
                    <li>
                      2: Low-card{' '}
                      <span className='float-end text-muted'>8 pts</span>
                    </li>
                    <li>
                      3: Mid-card{' '}
                      <span className='float-end text-muted'>12 pts</span>
                    </li>
                    <li>
                      4: Top-card{' '}
                      <span className='float-end text-muted'>16 pts</span>
                    </li>
                    <li>
                      5: Main Eventer{' '}
                      <span className='float-end text-muted'>20 pts</span>
                    </li>
                  </ul>
                  <div className='mt-2 pt-2 border-top'>
                    <small className='text-muted'>Massimo: 20 punti</small>
                  </div>
                </div>
              </div>

              <div className='col-md-4'>
                <div className='border rounded p-3 h-100'>
                  <h6 className='fw-bold border-bottom pb-2'>Storyline</h6>
                  <ul className='list-unstyled small mb-0'>
                    <li>
                      0: nessuna storyline{' '}
                      <span className='float-end text-muted'>0 pts</span>
                    </li>
                    <li>
                      1: inizio rivalità{' '}
                      <span className='float-end text-muted'>6 pts</span>
                    </li>
                    <li>
                      2: rivalità leggera{' '}
                      <span className='float-end text-muted'>12 pts</span>
                    </li>
                    <li>
                      3: in faida{' '}
                      <span className='float-end text-muted'>18 pts</span>
                    </li>
                    <li>
                      4: rivalità intensa{' '}
                      <span className='float-end text-muted'>24 pts</span>
                    </li>
                  </ul>
                  <div className='mt-2 pt-2 border-top'>
                    <small className='text-muted'>Massimo: 24 punti</small>
                  </div>
                </div>
              </div>

              <div className='col-md-4'>
                <div className='border rounded p-3 h-100'>
                  <h6 className='fw-bold border-bottom pb-2'>Titoli</h6>
                  <ul className='list-unstyled small mb-0'>
                    <li>
                      Campione normale{' '}
                      <span className='float-end text-muted'>3 pts</span>
                    </li>
                    <li>
                      Campione mondiale{' '}
                      <span className='float-end text-muted'>6 pts</span>
                    </li>
                  </ul>
                  <div className='mt-2 pt-2 border-top'>
                    <small className='text-muted'>
                      Massimo: 9 punti (se entrambi)
                    </small>
                  </div>
                </div>
              </div>
            </div>

            <div className='row mt-3'>
              <div className='col-md-6'>
                <div className='border rounded p-3'>
                  <h6 className='fw-bold border-bottom pb-2'>Fazioni</h6>
                  <p className='small mb-2'>
                    Moltiplicatore:{' '}
                    <span className='text-primary'>0.2 per fazione</span>
                  </p>
                  <p className='small mb-0'>
                    Esempio: 5 fazioni →{' '}
                    <span className='text-primary'>5 × 0.2 = 1 punto</span>
                  </p>
                  <p className='small mb-0'>
                    Massimo teorico:{' '}
                    <span className='text-primary'>10 fazioni → 2 punti</span>
                  </p>
                </div>
              </div>

              <div className='col-md-6'>
                <div className='border rounded p-3'>
                  <h6 className='fw-bold border-bottom pb-2'>Punti</h6>
                  <p className='small mb-2'>
                    Moltiplicatore:{' '}
                    <span className='text-primary'>0.1 per punto</span>
                  </p>
                  <p className='small mb-0'>
                    Massimo:{' '}
                    <span className='text-primary'>300 × 0.1 = 30 punti</span>
                  </p>
                  <p className='small mb-0'>
                    Esempio: 500 punti →{' '}
                    <span className='text-primary'>30 punti (massimo)</span>
                  </p>
                </div>
              </div>
            </div>

            <div className='mt-3 p-3 bg-light rounded'>
              <h6 className='fw-bold mb-2'>Punteggio Massimo Teorico</h6>
              <p className='small mb-1'>
                20 (Posizione) + 24 (Storyline) + 9 (Titoli) + 2 (Fazioni) + 30
                (Punti) = <span className='text-primary fw-bold'>85 punti</span>
              </p>
              <p className='small mb-0'>
                Il punteggio viene arrotondato matematicamente (≥0.5 per
                eccesso) e limitato tra 1 e 90 punti
              </p>
              <p className='small mb-0'>
                Esempio con 5 fazioni:{' '}
                <span className='text-primary'>
                  20 + 24 + 9 + 1 + 30 = 84 punti
                </span>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className='table-responsive mt-4 rounded-3 shadow-sm'>
        <table className='table table-striped table-hover align-middle mb-0'>
          <thead className='table-dark'>
            <tr>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light'
                onClick={() => handleSort('name')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center'>
                  Wrestler
                  {sortIcon('name')}
                </div>
              </th>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light text-center'
                onClick={() => handleSort('position')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center justify-content-center'>
                  Posizione
                  {sortIcon('position')}
                </div>
              </th>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light text-center'
                onClick={() => handleSort('storyline')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center justify-content-center'>
                  Storyline
                  {sortIcon('storyline')}
                </div>
              </th>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light text-center'
              >
                Campione
              </th>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light text-center'
              >
                Campione Mondiale
              </th>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light text-center'
                onClick={() => handleSort('factions')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center justify-content-center'>
                  Fazioni
                  {sortIcon('factions')}
                </div>
              </th>
              <th
                scope='col'
                className='ps-3 py-2 text-uppercase fw-bold text-light text-center'
                onClick={() => handleSort('score')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center justify-content-center'>
                  Punti
                  {sortIcon('score')}
                </div>
              </th>
              <th
                scope='col'
                className='pe-3 py-2 text-uppercase fw-bold text-light text-center'
                onClick={() => handleSort('calculated')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center justify-content-center'>
                  Nuova Quotazione
                  {sortIcon('calculated')}
                </div>
              </th>
              <th
                scope='col'
                className='pe-3 py-2 text-uppercase fw-bold text-light text-center'
                onClick={() => handleSort('quotation')}
                style={{ cursor: 'pointer' }}
              >
                <div className='d-flex align-items-center justify-content-center'>
                  Quotazione Attuale
                  {sortIcon('quotation')}
                </div>
              </th>
              <th
                scope='col'
                className='pe-3 py-2 text-uppercase fw-bold text-light text-center'
              >
                Azione
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedWrestlers.map(w => {
              const calculatedQuotation = calculateQuotationForWrestler({
                position:
                  editableFields.position[w.id] !== undefined
                    ? editableFields.position[w.id]
                    : w.position,
                storyline:
                  editableFields.storyline[w.id] !== undefined
                    ? editableFields.storyline[w.id]
                    : w.storyline,
                isChampion: w.isChampion,
                isWorldChampion: w.isWorldChampion,
                factions: factionCounts[w.id] || 0,
                score: w.score
              })

              const adjustment = manualAdjustments[w.id] || 0
              const finalQuotation = getFinalQuotation(w)

              return (
                <tr key={w.id}>
                  <td className='ps-3 py-2'>
                    <span className='fw-semibold text-dark'>{w.name}</span>
                  </td>
                  <td className='text-center'>
                    <select
                      className='form-select form-select-sm'
                      value={editableFields.position[w.id] || 0}
                      onChange={e =>
                        handleFieldChange(
                          w.id,
                          'position',
                          parseInt(e.target.value)
                        )
                      }
                    >
                      {[0, 1, 2, 3, 4, 5].map(val => (
                        <option key={val} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className='text-center'>
                    <select
                      className='form-select form-select-sm'
                      value={editableFields.storyline[w.id] || 0}
                      onChange={e =>
                        handleFieldChange(
                          w.id,
                          'storyline',
                          parseInt(e.target.value)
                        )
                      }
                    >
                      {[0, 1, 2, 3, 4].map(val => (
                        <option key={val} value={val}>
                          {val}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className='text-center'>
                    <div className='form-check d-flex justify-content-center'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={w.isChampion}
                        disabled
                      />
                    </div>
                  </td>
                  <td className='text-center'>
                    <div className='form-check d-flex justify-content-center'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={w.isWorldChampion}
                        disabled
                      />
                    </div>
                  </td>
                  <td className='text-center text-light'>
                    <span className='fw-semibold'>
                      {factionCounts[w.id] || 0}
                    </span>
                  </td>
                  <td className='text-center text-light'>
                    <span className='fw-semibold'>{w.score}</span>
                  </td>
                  <td className='text-center'>
                    <div className='d-flex align-items-center justify-content-center gap-1'>
                      <button
                        className='btn btn-sm border-0 py-0 px-2 text-light'
                        onClick={() => decrementAdjustment(w.id)}
                        disabled={saving || savingSingleId === w.id}
                        title='Diminuisci di 1'
                      >
                        <i className='bi bi-dash-circle'></i>
                      </button>

                      <div className='d-flex flex-column align-items-center'>
                        <span
                          className={`fw-bold ${
                            finalQuotation !== getCurrentPrice(w)
                              ? finalQuotation > getCurrentPrice(w)
                                ? 'text-warning'
                                : 'text-danger'
                              : 'text-success'
                          }`}
                        >
                          {finalQuotation}
                          {finalQuotation !== getCurrentPrice(w) &&
                            (finalQuotation > getCurrentPrice(w) ? (
                              <i
                                className='bi bi-caret-up-fill ms-1'
                                title='Quotazione in aumento'
                              ></i>
                            ) : (
                              <i
                                className='bi bi-caret-down-fill ms-1'
                                title='Quotazione in diminuzione'
                              ></i>
                            ))}
                        </span>
                      </div>

                      <button
                        className='btn btn-sm border-0 py-0 px-2 text-light'
                        onClick={() => incrementAdjustment(w.id)}
                        disabled={saving || savingSingleId === w.id}
                        title='Aumenta di 1'
                      >
                        <i className='bi bi-plus-circle'></i>
                      </button>
                    </div>
                  </td>
                  <td className='text-center'>
                    <span className='fw-bold text-success'>
                      {getCurrentPrice(w)}
                    </span>
                    <br />
                    <small className='text-muted'>
                      ({w.prices?.length || 0} storico)
                    </small>
                  </td>
                  <td className='text-center'>
                    <button
                      className='btn btn-sm btn-success'
                      onClick={() => doSaveSingle(w)}
                      disabled={saving || savingSingleId === w.id}
                    >
                      {savingSingleId === w.id ? 'Salvando...' : 'Salva'}
                    </button>
                    {singleSaveMessage[w.id] && (
                      <div
                        className={`mt-1 small ${
                          singleSaveMessage[w.id] === 'Errore'
                            ? 'text-danger'
                            : 'text-success'
                        }`}
                      >
                        {singleSaveMessage[w.id]}
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default EditPrice
